import { useState, type FormEvent } from 'react';

import { useAuth } from '../contexts/AuthContext';
import { TwoFactorPanel } from '../components/TwoFactorPanel';
import { ApiError, authApi } from '../services/api/client';

/**
 * Troca da própria senha (PRD 34.2) — professor ou aluno, mesma tela. Exige a
 * senha atual: sem isso, uma sessão esquecida aberta numa máquina
 * compartilhada bastaria para sequestrar a conta.
 */
export default function AccountPage() {
  const { user, refresh } = useAuth();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  if (!user) return null;

  /**
   * "Sair de todos os aparelhos" (PRD 38.1): derruba as sessões da conta em
   * todos os navegadores, inclusive este. O `refresh` consulta `/auth/me`, que
   * passa a responder 401; sem usuário, o `ProtectedRoute` leva ao login.
   */
  async function handleLogoutAll() {
    const confirmed = window.confirm(
      'Sair de todos os aparelhos? Você será desconectado aqui também e precisará entrar de novo em cada um.',
    );
    if (!confirmed) return;

    try {
      await authApi.logoutAll();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Falha ao encerrar as sessões.');
      return;
    }

    await refresh();
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError('');
    setSaved(false);

    if (newPassword !== confirmPassword) {
      setError('A confirmação não confere com a senha nova.');
      return;
    }

    setSaving(true);

    try {
      await authApi.changePassword(currentPassword, newPassword);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Falha ao trocar a senha.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <div className="section-head">
        <div>
          <h2>Conta</h2>
          <p className="muted">
            {user.name} · {user.email}
          </p>
        </div>
      </div>

      <section className="card">
        <h3>Trocar senha</h3>
        <p className="muted small">
          Peça a senha atual antes de trocar — assim, mesmo que alguém pegue seu computador com a
          sessão aberta, não consegue assumir a conta sozinho.
        </p>

        <form onSubmit={handleSubmit} noValidate>
          <label htmlFor="current-password">Senha atual</label>
          <input
            id="current-password"
            type="password"
            autoComplete="current-password"
            required
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
          />

          <label htmlFor="new-password">Senha nova</label>
          <input
            id="new-password"
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />

          <label htmlFor="confirm-password">Confirmar senha nova</label>
          <input
            id="confirm-password"
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />

          {error && <p className="form-error">{error}</p>}

          <div className="button-row" style={{ marginTop: '0.75rem' }}>
            <button type="submit" className="primary" disabled={saving}>
              {saving ? 'Salvando…' : 'Trocar senha'}
            </button>
            {saved && <span className="muted small">Senha trocada.</span>}
          </div>
        </form>
      </section>

      {/* Verificação em dois passos (PRD 39.2): só professor. O backend exige o
          papel de qualquer forma — esconder aqui é só para não mostrar uma
          tela que devolveria 403. */}
      {user.role === 'PROFESSOR' && <TwoFactorPanel />}

      <section className="card">
        <h3>Sessões</h3>
        <p className="muted small">
          Você fica conectado por até 7 dias em cada aparelho em que entrou. Se usou um computador
          emprestado ou suspeita que alguém acessou sua conta, encerre todas as sessões. Trocar a
          senha também desconecta os outros aparelhos.
        </p>
        <div className="button-row">
          <button type="button" onClick={() => void handleLogoutAll()}>
            Sair de todos os aparelhos
          </button>
        </div>
      </section>
    </>
  );
}
